/**
 * Client-side redaction for response data before ANY of it is sent to the AI model.
 *
 * P3 privacy model: the model can only read a response body when the user opts in
 * ("Let AI read responses"), and even then the body is scrubbed here first. This
 * module never makes network calls — it only sanitizes an in-memory response.
 *
 * Redaction is deliberately privacy-leaning (a few false positives like git SHAs
 * are acceptable): better to over-redact than to leak a token.
 */

export const REDACTED = '«redacted»';

/** Default cap on redacted body characters returned to the model. */
export const AI_BODY_CHAR_LIMIT = 20000;

/** Response headers safe to reveal (everything else is dropped). */
const SAFE_HEADER_ALLOWLIST = new Set([
  'content-type', 'content-length', 'content-encoding', 'content-language',
  'date', 'cache-control', 'expires', 'last-modified', 'etag', 'age', 'vary',
  'server', 'connection', 'transfer-encoding', 'accept-ranges',
  'x-request-id', 'x-correlation-id',
  'x-ratelimit-limit', 'x-ratelimit-remaining', 'x-ratelimit-reset', 'retry-after',
]);

/** Object keys whose values are always redacted, regardless of value shape. */
const SENSITIVE_KEY_RE =
  /(pass(word)?|secret|token|api[-_ ]?key|auth(orization)?|bearer|credential|client[-_ ]?secret|refresh[-_ ]?token|access[-_ ]?token|session[-_ ]?id|set[-_ ]?cookie|cookie|private[-_ ]?key|signature|otp|pin\b|ssn|card[-_ ]?number|cvv)/i;

// Value patterns that look like secrets even without a telltale key name.
const VALUE_PATTERNS = [
  // PEM private key blocks
  /-----BEGIN [^-]*PRIVATE KEY-----[\s\S]*?-----END [^-]*PRIVATE KEY-----/g,
  // JWTs (three base64url segments)
  /\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\b/g,
  // "Bearer <token>" / "Basic <token>" in free text
  /\b(?:Bearer|Basic)\s+[A-Za-z0-9._~+/-]{8,}=*/g,
  // AWS access key ids
  /\bAKIA[0-9A-Z]{16}\b/g,
  // Long hex blobs (api keys, sha512, etc.)
  /\b[0-9a-fA-F]{32,}\b/g,
  // Long base64-ish blobs (no trailing \b — base64 '=' padding isn't a word boundary)
  /\b[A-Za-z0-9+/]{40,}={0,2}/g,
];

// user:pass@host credentials embedded in URLs.
const URL_CRED_RE = /([a-z][a-z0-9+.-]*:\/\/)[^/\s:@]+:[^/\s:@]+@/gi;

/**
 * Redact secret-looking substrings inside a string.
 * @returns {{ value: string, count: number }}
 */
export function redactString(input) {
  if (typeof input !== 'string' || !input) return { value: input, count: 0 };
  let count = 0;
  const urlMatches = input.match(URL_CRED_RE);
  if (urlMatches) count += urlMatches.length;
  let out = input.replace(URL_CRED_RE, (_m, scheme) => `${scheme}${REDACTED}@`);
  for (const re of VALUE_PATTERNS) {
    const matches = out.match(re);
    if (matches) count += matches.length;
    out = out.replace(re, REDACTED); // REDACTED has no `$`, safe as a literal replacement
  }
  return { value: out, count };
}

/** Recursively redact a parsed JSON value. Mutates nothing; returns a copy. */
function redactValue(value, counter, depth) {
  if (depth > 20) return value;
  if (Array.isArray(value)) {
    return value.map((v) => redactValue(v, counter, depth + 1));
  }
  if (value && typeof value === 'object') {
    const out = {};
    for (const [key, val] of Object.entries(value)) {
      if (SENSITIVE_KEY_RE.test(key)) {
        out[key] = REDACTED;
        counter.count += 1;
      } else {
        out[key] = redactValue(val, counter, depth + 1);
      }
    }
    return out;
  }
  if (typeof value === 'string') {
    const { value: red, count } = redactString(value);
    counter.count += count;
    return red;
  }
  return value;
}

/** Keep only allowlisted headers; report how many were dropped. */
export function redactHeaders(headers) {
  const safe = {};
  let dropped = 0;
  for (const [key, val] of Object.entries(headers || {})) {
    if (SAFE_HEADER_ALLOWLIST.has(String(key).toLowerCase())) {
      safe[key] = val;
    } else {
      dropped += 1;
    }
  }
  return { headers: safe, dropped_header_count: dropped };
}

/**
 * Redact a response body string. JSON is parsed and key/value-redacted; other
 * bodies get substring redaction. Always returns a string + a redaction count.
 * @returns {{ body: string, count: number, is_json: boolean }}
 */
export function redactBody(bodyText) {
  const text = typeof bodyText === 'string' ? bodyText : String(bodyText ?? '');
  const trimmed = text.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(text);
      const counter = { count: 0 };
      const redacted = redactValue(parsed, counter, 0);
      return { body: JSON.stringify(redacted, null, 2), count: counter.count, is_json: true };
    } catch {
      /* not valid JSON — fall through to string redaction */
    }
  }
  const { value, count } = redactString(text);
  return { body: value, count, is_json: false };
}

/**
 * Produce an AI-safe view of a response: allowlisted headers + redacted, truncated
 * body. Never mutates the input.
 *
 * @param {object} response { status, statusText, headers, data }
 * @param {{ maxChars?: number }} [opts]
 * @returns {{ status, status_text, content_type, headers, body, body_truncated, redaction_count, dropped_header_count }}
 */
export function redactResponse(response, { maxChars = AI_BODY_CHAR_LIMIT } = {}) {
  const res = response || {};
  const rawBody = typeof res.data === 'string' ? res.data : JSON.stringify(res.data ?? '');
  const { body: redactedBody, count } = redactBody(rawBody);
  const truncated = redactedBody.length > maxChars;
  const { headers, dropped_header_count } = redactHeaders(res.headers);
  const ct = res.headers && (res.headers['content-type'] || res.headers['Content-Type']);
  return {
    status: res.status ?? null,
    status_text: res.statusText ?? res.status_text ?? '',
    content_type: ct || null,
    headers,
    body: truncated ? redactedBody.slice(0, maxChars) : redactedBody,
    body_truncated: truncated,
    redaction_count: count,
    dropped_header_count,
  };
}
